import { IUserDto, ICharacterDto } from 'shared/interfaces';
import { AppLanguage } from 'shared/i18n';
import BaseApiService from './BaseApiService';
import { getAuthToken } from './tokenStorage';


export interface UserParametersDto {
  id: string;
  email: string;
  birth_date: string;
  gender: string;
  height_cm: number;
  weight_kg: number;
  bmi: number;
  handedness: string;
  activity_level: string;
  restrictions: string[];
  language: AppLanguage;
  body_fat_percent: number | null;
  lean_body_mass_kg: number | null;
  skeletal_muscle_mass_kg: number | null;
  waist_circumference_cm: number | null;
  hip_circumference_cm: number | null;
  resting_heart_rate_bpm: number | null;
  systolic_bp_mm_hg: number | null;
  diastolic_bp_mm_hg: number | null;
  fasting_glucose_mmol_l: number | null;
  hba1c_percent: number | null;
  fasting_insulin_miu_l: number | null;
  total_cholesterol_mmol_l: number | null;
  ldl_cholesterol_mmol_l: number | null;
  hdl_cholesterol_mmol_l: number | null;
  triglycerides_mmol_l: number | null;
  hemoglobin_g_l: number | null;
  ferritin_ug_l: number | null;
  creatinine_umol_l: number | null;
  vitamin_d_ng_ml: number | null;
  vo2max_ml_kg_min: number | null;
  default_medball_mass_kg: number | null;
  health_measurements_date: string | null;
  saved?: boolean;
  wellness_profile_synced?: boolean;
  wellness_sync_warning?: string;
  health_history_saved?: boolean;
  health_history_date?: string;
  psychological_twin_synced?: boolean;
  psychological_points_synced?: number;
  psychological_sync_warning?: string;
  profile_source?: 'backend';
  server_time?: string;
}

export type UpdateUserParametersRequest = Omit<
  UserParametersDto,
  | 'id'
  | 'bmi'
  | 'lean_body_mass_kg'
  | 'saved'
  | 'wellness_profile_synced'
  | 'wellness_sync_warning'
  | 'health_history_saved'
  | 'health_history_date'
  | 'psychological_twin_synced'
  | 'psychological_points_synced'
  | 'psychological_sync_warning'
  | 'profile_source'
  | 'server_time'
>;

export interface UserHealthMeasurementDto {
  id: string;
  measured_at: string;
  height_cm: number | null;
  weight_kg: number | null;
  body_fat_percent: number | null;
  skeletal_muscle_mass_kg: number | null;
  waist_circumference_cm: number | null;
  hip_circumference_cm: number | null;
  resting_heart_rate_bpm: number | null;
  systolic_bp_mm_hg: number | null;
  diastolic_bp_mm_hg: number | null;
  fasting_glucose_mmol_l: number | null;
  hba1c_percent: number | null;
  fasting_insulin_miu_l: number | null;
  total_cholesterol_mmol_l: number | null;
  ldl_cholesterol_mmol_l: number | null;
  hdl_cholesterol_mmol_l: number | null;
  triglycerides_mmol_l: number | null;
  hemoglobin_g_l: number | null;
  ferritin_ug_l: number | null;
  creatinine_umol_l: number | null;
  vitamin_d_ng_ml: number | null;
  vo2max_ml_kg_min: number | null;
  source: string;
  created_at: string;
  updated_at: string;
}

export interface UserHealthMeasurementHistoryResponse {
  items: UserHealthMeasurementDto[];
  count: number;
  source: 'backend';
}

export interface LanguageSettingResponse {
  language: AppLanguage;
  supported_languages: AppLanguage[];
}

class UsersApiService extends BaseApiService {
  constructor() {
    super('api');
  }

  // 🔹 Получение профиля текущего пользователя
  getProfile(): Promise<IUserDto> {
    return this.GETBYID('profile', {});
  }

  private getAuthorizedRequestHeaders(): { Authorization: string } {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authorization token is missing. Sign in again.');
    }
    return { Authorization: `Bearer ${token}` };
  }

  getUserParameters(): Promise<UserParametersDto> {
    return this.GET<UserParametersDto>('user-parameters', {
      headers: this.getAuthorizedRequestHeaders(),
      // Cache busting without custom request headers: custom Cache-Control/Pragma
      // headers trigger a CORS preflight on older backend builds.
      params: { _ts: Date.now() },
    });
  }

  updateUserParameters(payload: UpdateUserParametersRequest): Promise<UserParametersDto> {
    return this.PUT<UserParametersDto, UpdateUserParametersRequest>('user-parameters', payload, {
      headers: this.getAuthorizedRequestHeaders(),
    });
  }

  getUserParametersHistory(days = 3650): Promise<UserHealthMeasurementHistoryResponse> {
    return this.GET<UserHealthMeasurementHistoryResponse>('user-parameters/history', {
      headers: this.getAuthorizedRequestHeaders(),
      params: { days, _ts: Date.now() },
    });
  }

  // 🔹 Получение серверного языка пользователя
  getLanguageSetting(): Promise<LanguageSettingResponse> {
    return this.GET<LanguageSettingResponse>('settings/language');
  }

  // 🔹 Обновление серверного языка пользователя
  updateLanguageSetting(language: AppLanguage): Promise<LanguageSettingResponse> {
    return this.PUT<LanguageSettingResponse>('settings/language', { language });
  }

  // 🔹 Получение всех персонажей пользователя
  getCharacters(): Promise<ICharacterDto[]> {
    return this.GETBYID('characters/', {});
  }

  // 🔹 Получение CSV данных для персонажа по уровню
  getCharacterCSV(characterId: number, level: string): Promise<any[]> {
    return this.POST(`characters/${characterId}/csv/`, { level_name: level });
  }

  // 🔹 Получение gesture данных для персонажа
  getCharacterGesture(characterId: number, level: string): Promise<any[]> {
    return this.POST(`characters/${characterId}/gesture/`, { level_name: level });
  }

  // 🔹 Получение статистики персонажа
  getCharacterStat(characterId: number, level: string): Promise<any[]> {
    return this.POST(`characters/${characterId}/stat/`, { level_name: level });
  }
}

export default new UsersApiService();
